import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { apiErrorMessage, launchApi } from '../api';
import { ProductDetail, ProductDuplicate, ProductFacet } from '../types';
import { productCommandId } from './productLabels';

interface Props {
  open: boolean;
  facets: { lists: ProductFacet[]; groups: ProductFacet[]; suppliers: ProductFacet[] };
  defaults: { list: string; group: string; supplier: string };
  onClose: () => void;
  onCreated: (detail: ProductDetail) => void;
}

const FreeText: React.FC<{ label: string; value: string; options: ProductFacet[]; required?: boolean; onChange: (value: string) => void }> = ({ label, value, options, required, onChange }) => (
  <Autocomplete
    freeSolo
    options={options.map((item) => item.name)}
    inputValue={value}
    onInputChange={(_event, next) => onChange(next)}
    renderInput={(params) => <TextField {...params} size="small" label={label} required={required} />}
  />
);

const NewProductDialog: React.FC<Props> = ({ open, facets, defaults, onClose, onCreated }) => {
  const [list, setList] = useState('');
  const [group, setGroup] = useState('');
  const [supplier, setSupplier] = useState('');
  const [workingName, setWorkingName] = useState('');
  const [articleNumber, setArticleNumber] = useState('');
  const [duplicates, setDuplicates] = useState<ProductDuplicate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [commandId, setCommandId] = useState('');

  useEffect(() => {
    if (!open) return;
    setList(defaults.list || facets.lists[0]?.name || 'DVH');
    setGroup(defaults.group);
    setSupplier(defaults.supplier);
    setWorkingName('');
    setArticleNumber('');
    setDuplicates([]);
    setError(null);
    setCommandId(productCommandId());
  }, [defaults.group, defaults.list, defaults.supplier, facets.lists, open]);

  const submit = async (allowDuplicate: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const detail = await launchApi.createProductInfo({
        commandId,
        allowDuplicate,
        patch: { list, group, supplier, articleNumber, fields: workingName.trim() ? { workingName } : {} },
      });
      onCreated(detail);
    } catch (requestError: any) {
      const data = requestError?.response?.data;
      if (requestError?.response?.status === 409 && Array.isArray(data?.duplicates)) setDuplicates(data.duplicates);
      else setError(apiErrorMessage(requestError, 'Produkten kunde inte skapas.'));
    } finally {
      setBusy(false);
    }
  };

  const ready = Boolean(list.trim() && articleNumber.trim());

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ny produkt</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: 13 }}>Fyll i det du vet nu. Resten skriver du in på produktkortet.</Typography>
          <TextField
            size="small"
            label="Artikelnummer"
            required
            autoFocus
            value={articleNumber}
            onChange={(event) => { setArticleNumber(event.target.value); setDuplicates([]); }}
            inputProps={{ maxLength: 30 }}
          />
          <TextField size="small" label="Arbetsnamn" value={workingName} onChange={(event) => setWorkingName(event.target.value)} inputProps={{ maxLength: 120 }} />
          <FreeText label="Leverantör" value={supplier} options={facets.suppliers} onChange={setSupplier} />
          <FreeText label="Varugrupp" value={group} options={facets.groups} onChange={setGroup} />
          <FreeText label="Lista" value={list} options={facets.lists} required onChange={setList} />
          {duplicates.length > 0 && (
            <Alert severity="warning">
              Artikelnumret finns redan:
              {duplicates.map((item) => <div key={item.id}>{item.list}: {item.articleNumber} {item.name}</div>)}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Avbryt</Button>
        {duplicates.length > 0 ? (
          <Button variant="contained" disabled={busy || !ready} onClick={() => submit(true)}>Skapa ändå</Button>
        ) : (
          <Button variant="contained" disabled={busy || !ready} onClick={() => submit(false)}>Skapa</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewProductDialog;
